import { useNavigate } from 'react-router-dom';

import { Formik, FormikProvider } from 'formik';
import moment from 'moment';
import { useRecoilValue } from 'recoil';

import { getAppointments as getAppointmentsApi } from 'api/Appointment';

import { useDashboardContext } from 'lib/context/Dashboard/DashboardContext';
import { iaRa } from 'lib/helpers/utility';
import { usePractitioners } from 'lib/hooks/queries/practitioners/usePractitioners';

import {
  appointmentStatuses as aptStatuses,
  currentPractice
} from 'components/practice/practiceState';
import { createQueryFilters } from 'components/shared/AgTablePage/lib/createQueryFilters';
import exportToCsv from 'components/shared/Export/lib/exportToCsv';
import exportToExcel from 'components/shared/Export/lib/exportToExcel';
import exportToPdf from 'components/shared/Export/lib/exportToPdf';
import Icon from 'components/shared/Icon/Icon';

import columns from '../../lib/calendarExportColumns';
import transformAppointmentsToExportDataFormat from '../../lib/transformAppointmentsToExportDataFormat';
import validationSchema from '../../lib/validationSchema';
import Footer from '../Footer/Footer';

import {
  // ActiveFilterList,
  ExportColumnsCheckbox,
  ExportInputTitle,
  ExportTypeCheckbox
} from './components';
import ExportDateRange from './components/ExportDateRange';

const Content = ({ closePopover, exportRange = [] }) => {
  const practice = useRecoilValue(currentPractice);
  const { filters } = useDashboardContext();
  const navigate = useNavigate();
  const appointmentStatuses = useRecoilValue(aptStatuses);

  const initialValues = {
    title: `Calendar Export ${moment(exportRange[0]).format('MM/DD/YYYY')} - ${moment(
      exportRange[1]
    ).format('MM/DD/YYYY')}`,
    export_range: exportRange,
    export_type: [],
    export_columns: ['all'],
    selected_columns: []
  };
  const { data } = usePractitioners();
  const practitioners = iaRa(data?.practitioners);

  const fieldToHeaderMap = {
    datetime: 'Datetime',
    patient_id: 'Patient ID',
    patient: 'Patient',
    event_name: 'Event Name',
    insurance_name: 'Insurance Name',
    service: 'Service',
    cpt_code: 'Cpt Code',
    provider: 'Provider',
    balance: 'Balance',
    status: 'Status'
  };

  const getAppointments = async (export_range) => {
    const params = {
      rangeDate: {
        startDate: export_range[0],
        endDate: export_range[1]
      },
      exporting: true,
      withEvents: true,
      filters: createQueryFilters(filters)
    };

    const data = await getAppointmentsApi({ ...params, navigate });

    return {
      appointments: [
        ...data.appointment.map((apt) => ({
          ...apt,
          start_date: apt.starts_at,
          end_date: apt.ends_at,
          type: 'appointment',
          sort_date: new Date(apt.starts_at)
        })),
        ...data.events.map((event) => ({
          ...event,
          type: 'event',
          sort_date: new Date(event.start_date)
        }))
      ].sort((a, b) => a.sort_date - b.sort_date),

      exportOnlyAppointments: data.exportOnlyAppointments,
      exportOnlyEvents: data.exportOnlyEvents
    };
  };

  const extractedDataFromSelectedColumns = (
    data,
    selectedColumns,
    exportOnlyAppointments,
    exportOnlyEvents
  ) => {
    if (exportOnlyAppointments) {
      selectedColumns = selectedColumns.filter((col) => col.field !== 'event_name');
    } else if (exportOnlyEvents) {
      selectedColumns = selectedColumns.filter(
        (col) => col.field === 'event_name' || col.field === 'datetime' || col.field === 'provider'
      );
    }
    return data.map((item) => {
      const result = {};

      selectedColumns.forEach((column) => {
        result[column.field] = item[column.field];
      });
      return result;
    });
  };

  const handleExport = async (values, otherProps) => {
    try {
      otherProps.setSubmitting(true);
      const {
        exportOnlyAppointments,
        exportOnlyEvents,
        appointments: data
      } = await getAppointments(values.export_range);
      const isExportAllColumns = values.export_columns.includes('all');

      const sortedSelectedColumns = values.selected_columns.sort((a, b) => {
        return (
          columns.findIndex((col) => col.field === a) - columns.findIndex((col) => col.field === b)
        );
      });

      const columnToSelect = isExportAllColumns
        ? columns
        : sortedSelectedColumns.map((col) => {
            return columns.find((column) => column.field === col);
          });

      const transformedData = transformAppointmentsToExportDataFormat(
        data,
        appointmentStatuses,
        practitioners
      );
      const extractedData = extractedDataFromSelectedColumns(
        transformedData,
        columnToSelect,
        exportOnlyAppointments,
        exportOnlyEvents
      );

      const selectedColumnsFromData = extractedData.map((obj) => {
        return Object.fromEntries(
          Object.entries(obj).map(([key, value]) => [fieldToHeaderMap[key] || key, value])
        );
      });
      if (values.export_type.includes('csv')) {
        exportToCsv(selectedColumnsFromData, values.title);
      }

      if (values.export_type.includes('excel')) {
        exportToExcel(selectedColumnsFromData, values.title);
      }

      if (values.export_type.includes('pdf')) {
        exportToPdf(selectedColumnsFromData, values.title, practice);
      }

      otherProps.setSubmitting(false);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, otherProps) => handleExport(values, otherProps)}
      validationSchema={validationSchema}>
      {(formValues) => (
        <FormikProvider value={{ ...formValues, columns }}>
          <div className="flex flex-col ">
            <div className="flex justify-between gap-4 bg-white px-4 py-2 shadow-[0px_2px_4px_rgba(27,33,35,0.04)]">
              <span className="text-base font-500 leading-6 text-primary-900">Export Report</span>
              <div
                className="ml-auto flex size-[26px] cursor-pointer select-none items-center justify-center"
                onClick={closePopover}>
                <Icon
                  data-qa="new-close-square"
                  icon="new-close"
                  className="cursor-pointer"
                  size={10}
                  onClick={closePopover}
                />
              </div>
            </div>

            <div className="flex max-h-[400px] flex-col gap-4 overflow-y-auto p-4">
              <ExportInputTitle />
              <ExportDateRange />
              <ExportTypeCheckbox />
              <ExportColumnsCheckbox />
              {/* <ActiveFilterList /> */}
            </div>
          </div>
          <Footer closePopover={closePopover} />
        </FormikProvider>
      )}
    </Formik>
  );
};

export default Content;
